import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Button,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import apiClient from '../api/client';

interface ActivityEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  old_value: any;
  new_value: any;
  created_at: string;
  username: string;
  display_name: string;
  role: string;
  user_email: string;
}

// ── Helpers ──

function parse(val: any): any {
  if (!val) return null;
  return typeof val === 'string' ? JSON.parse(val) : val;
}

function getEntityName(nv: any, ov: any, entityType: string): string {
  if (entityType === 'Client') return nv?.client_name || ov?.client_name || '';
  if (entityType === 'Admin') return nv?.display_name || ov?.display_name || nv?.username || ov?.username || '';
  return nv?.name || ov?.name || '';
}

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthYear(nv: any): string {
  if (!nv?.month || !nv?.year) return '';
  return ` (${MONTH_NAMES[nv.month]} ${nv.year})`;
}

function formatEntryDate(dateVal: any): string {
  if (!dateVal) return '';
  const str = typeof dateVal === 'string' ? dateVal : String(dateVal);
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Who column ──

function getRoleLabel(entry: ActivityEntry): string {
  const role = entry.role;
  if (role === 'admin' || role === 'super_admin') return 'Admin';
  if (role === 'employee') return 'Employee';
  if (role === 'customer') return 'Client';
  return 'System';
}

function getRoleChipColor(label: string): { bg: string; color: string } {
  switch (label) {
    case 'Admin': return { bg: '#EFF6FF', color: '#1E40AF' };
    case 'Employee': return { bg: '#F0FDF4', color: '#166534' };
    case 'Client': return { bg: '#FFF7ED', color: '#9A3412' };
    default: return { bg: '#F1F5F9', color: '#64748B' };
  }
}

// ── Diff helper: show what changed between old and new values ──

const FIELD_LABELS: Record<string, string> = {
  name: 'name', email: 'email', job_title: 'job title', employee_location: 'location',
  hourly_rate: 'hourly rate', status: 'status', client_id: 'client', start_date: 'start date',
  expected_daily_hours: 'daily hours', contact_number: 'phone', is_test: 'test flag',
  client_name: 'name', client_location: 'location', client_website: 'website',
  engagement_type: 'engagement', primary_contact_name: 'primary contact',
  primary_contact_email: 'primary email', secondary_contact_name: 'secondary contact',
  secondary_contact_email: 'secondary email', preferred_working_hours: 'working hours',
  display_name: 'display name', username: 'username', role: 'role',
  is_active: 'active status',
};

const SKIP_FIELDS = new Set([
  'id', 'uid', 'user_id', 'created_at', 'updated_at', 'created_by', 'updated_by',
  'plain_password', 'client_code', 'notes', 'action',
  'employee_name', 'month', 'year', 'latest_date', 'to', 'success',
]);

function getChangedFields(ov: any, nv: any): string {
  if (!ov || !nv) return '';
  const changes: string[] = [];
  for (const key of Object.keys(nv)) {
    if (SKIP_FIELDS.has(key)) continue;
    const oldVal = ov[key];
    const newVal = nv[key];
    if (String(oldVal ?? '') !== String(newVal ?? '') && FIELD_LABELS[key]) {
      changes.push(FIELD_LABELS[key]);
    }
  }
  if (changes.length === 0) return '';
  if (changes.length <= 3) return changes.join(', ');
  return `${changes.slice(0, 2).join(', ')} +${changes.length - 2} more`;
}

// ── Build human-readable description ──

interface ActivityInfo {
  icon: React.ReactNode;
  color: string;
  description: React.ReactNode;
}

function buildActivity(entry: ActivityEntry): ActivityInfo {
  const nv = parse(entry.new_value);
  const ov = parse(entry.old_value);
  const actor = entry.display_name || entry.username || 'System';
  const entityType = entry.entity_type;
  const action = entry.action;

  const bold = (text: string) => <strong>{text}</strong>;
  const muted = (text: string) => <span style={{ color: '#94A3B8' }}>{text}</span>;

  // ── Login ──
  if (action === 'login') {
    if (nv?.success === true) {
      return {
        icon: <LoginIcon sx={{ fontSize: 16 }} />,
        color: '#10B981',
        description: <>{bold(actor)} logged in</>,
      };
    }
    const attemptedUser = nv?.username || actor;
    return {
      icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
      color: '#EF4444',
      description: <>{bold(attemptedUser)} failed login attempt</>,
    };
  }

  // ── Employee ──
  if (entityType === 'Employee') {
    const name = getEntityName(nv, ov, entityType);
    if (action === 'create') {
      return {
        icon: <PersonAddIcon sx={{ fontSize: 16 }} />,
        color: '#10B981',
        description: <>{bold(actor)} created employee {bold(name)}</>,
      };
    }
    if (action === 'update') {
      if (nv?.action === 'password_reset') {
        return {
          icon: <LockResetIcon sx={{ fontSize: 16 }} />,
          color: '#F59E0B',
          description: <>{bold(actor)} reset password for {bold(name || 'employee')}</>,
        };
      }
      const changed = getChangedFields(ov, nv);
      return {
        icon: <EditIcon sx={{ fontSize: 16 }} />,
        color: '#3B82F6',
        description: <>{bold(actor)} updated employee {bold(name)}{changed ? <> — {muted(changed)}</> : ''}</>,
      };
    }
    if (action === 'delete') {
      const deletedName = getEntityName(ov, nv, entityType);
      if (nv?.status === 'inactive' || nv?.is_active === false) {
        return {
          icon: <BlockIcon sx={{ fontSize: 16 }} />,
          color: '#F59E0B',
          description: <>{bold(actor)} deactivated employee {bold(deletedName)}</>,
        };
      }
      return {
        icon: <DeleteIcon sx={{ fontSize: 16 }} />,
        color: '#EF4444',
        description: <>{bold(actor)} permanently deleted employee {bold(deletedName)}</>,
      };
    }
  }

  // ── Client ──
  if (entityType === 'Client') {
    const name = getEntityName(nv, ov, entityType);
    if (action === 'create') {
      return {
        icon: <PersonAddIcon sx={{ fontSize: 16 }} />,
        color: '#10B981',
        description: <>{bold(actor)} created client {bold(name)}</>,
      };
    }
    if (action === 'update') {
      if (nv?.action === 'customer_password_reset') {
        return {
          icon: <LockResetIcon sx={{ fontSize: 16 }} />,
          color: '#F59E0B',
          description: <>{bold(actor)} reset customer portal password for {bold(name)}</>,
        };
      }
      const changed = getChangedFields(ov, nv);
      return {
        icon: <EditIcon sx={{ fontSize: 16 }} />,
        color: '#3B82F6',
        description: <>{bold(actor)} updated client {bold(name)}{changed ? <> — {muted(changed)}</> : ''}</>,
      };
    }
    if (action === 'delete') {
      const deletedName = getEntityName(ov, nv, entityType);
      return {
        icon: <BlockIcon sx={{ fontSize: 16 }} />,
        color: '#F59E0B',
        description: <>{bold(actor)} deactivated client {bold(deletedName)}</>,
      };
    }
  }

  // ── Timesheet ──
  if (entityType === 'Timesheet') {
    if (nv?.action === 'email_sent') {
      return {
        icon: <EmailIcon sx={{ fontSize: 16 }} />,
        color: '#8B5CF6',
        description: <>{bold(actor)} emailed timesheet to {muted(nv.to)}</>,
      };
    }
    if (nv?.action === 'entry_saved') {
      const tillDate = formatEntryDate(nv.latest_date);
      return {
        icon: <SaveIcon sx={{ fontSize: 16 }} />,
        color: '#3B82F6',
        description: <>{bold(actor)} updated timesheet{nv?.employee_name ? <> for {bold(nv.employee_name)}</> : ''}{tillDate ? <> till {muted(tillDate)}</> : ''}</>,
      };
    }
    if (action === 'update') {
      const status = nv?.status;
      const period = formatMonthYear(nv);
      if (status === 'submitted') {
        return {
          icon: <SendIcon sx={{ fontSize: 16 }} />,
          color: '#F59E0B',
          description: <>{bold(actor)} submitted timesheet{nv?.employee_name ? <> for {bold(nv.employee_name)}</> : ''}{period ? muted(period) : ''}</>,
        };
      }
      if (status === 'approved') {
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          color: '#10B981',
          description: <>{bold(actor)} approved timesheet{nv?.employee_name ? <> for {bold(nv.employee_name)}</> : ''}{period ? muted(period) : ''}</>,
        };
      }
      if (status === 'rejected') {
        return {
          icon: <BlockIcon sx={{ fontSize: 16 }} />,
          color: '#EF4444',
          description: <>{bold(actor)} rejected timesheet{nv?.employee_name ? <> for {bold(nv.employee_name)}</> : ''}{period ? muted(period) : ''}</>,
        };
      }
    }
  }

  // ── Admin ──
  if (entityType === 'Admin') {
    const name = getEntityName(nv, ov, entityType);
    if (action === 'create') {
      return {
        icon: <PersonAddIcon sx={{ fontSize: 16 }} />,
        color: '#10B981',
        description: <>{bold(actor)} created admin {bold(name)}</>,
      };
    }
    if (action === 'update') {
      if (nv?.action === 'password_reset') {
        return {
          icon: <LockResetIcon sx={{ fontSize: 16 }} />,
          color: '#F59E0B',
          description: <>{bold(actor)} reset admin password for {bold(name)}</>,
        };
      }
      if (nv?.action === 'password_changed') {
        return {
          icon: <LockResetIcon sx={{ fontSize: 16 }} />,
          color: '#3B82F6',
          description: <>{bold(actor)} changed their password</>,
        };
      }
      const changed = getChangedFields(ov, nv);
      return {
        icon: <EditIcon sx={{ fontSize: 16 }} />,
        color: '#3B82F6',
        description: <>{bold(actor)} updated admin {bold(name)}{changed ? <> — {muted(changed)}</> : ''}</>,
      };
    }
    if (action === 'delete') {
      const deletedName = getEntityName(ov, nv, entityType);
      return {
        icon: <BlockIcon sx={{ fontSize: 16 }} />,
        color: '#F59E0B',
        description: <>{bold(actor)} deactivated admin {bold(deletedName)}</>,
      };
    }
  }

  // ── User (password change) ──
  if (entityType === 'User' && action === 'update') {
    if (nv?.action === 'password_changed') {
      return {
        icon: <LockResetIcon sx={{ fontSize: 16 }} />,
        color: '#3B82F6',
        description: <>{bold(actor)} changed their password</>,
      };
    }
  }

  // ── Fallback ──
  const entityName = getEntityName(nv, ov, entityType);
  return {
    icon: <EditIcon sx={{ fontSize: 16 }} />,
    color: '#94A3B8',
    description: <>{bold(actor)} {action} {entityType.toLowerCase()}{entityName ? ` ${entityName}` : ''}</>,
  };
}

// ── Time formatting ──

function formatTimeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  // Convert to IST
  const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000));
  const day = ist.getUTCDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[ist.getUTCMonth()];
  let hours = ist.getUTCHours();
  const minutes = ist.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  const suffix = day === 1 || day === 21 || day === 31 ? 'st'
    : day === 2 || day === 22 ? 'nd'
    : day === 3 || day === 23 ? 'rd'
    : 'th';

  return `${day}${suffix} ${month}, ${hours}:${minutes} ${ampm} IST`;
}

// ── Month grouping ──

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getMonthKey(dateStr: string): string {
  const d = new Date(dateStr);
  // Convert to IST
  const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000));
  const y = ist.getUTCFullYear();
  const m = (ist.getUTCMonth() + 1).toString().padStart(2, '0');
  return `${y}-${m}`;
}

function getMonthLabel(key: string): string {
  const [y, m] = key.split('-');
  return `${FULL_MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

interface MonthGroup {
  key: string;
  label: string;
  entries: ActivityEntry[];
}

function groupByMonth(entries: ActivityEntry[]): MonthGroup[] {
  const map = new Map<string, ActivityEntry[]>();
  for (const entry of entries) {
    const key = getMonthKey(entry.created_at);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(entry);
  }
  // Sort keys descending (newest first)
  const sorted = Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  return sorted.map(([key, items]) => ({ key, label: getMonthLabel(key), entries: items }));
}

// ── Main component ──

const PAGE_SIZE = 200;

export default function AdminActivityLogsPage() {
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  const fetchActivity = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/timesheets/activity?limit=${PAGE_SIZE}&offset=0`);
      setActivity(data.rows);
      setTotal(data.total);
    } catch (err) {
      console.error('Activity fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await apiClient.get(`/timesheets/activity?limit=${PAGE_SIZE}&offset=${activity.length}`);
      setActivity(prev => [...prev, ...data.rows]);
      setTotal(data.total);
    } catch (err) {
      console.error('Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const monthGroups = useMemo(() => groupByMonth(activity), [activity]);

  // Current month starts expanded, older months start collapsed
  useEffect(() => {
    if (monthGroups.length > 0) {
      setExpandedMonths(new Set([monthGroups[0].key]));
    }
  }, [monthGroups.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMonth = (key: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Activity Logs</Typography>
        <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>System activity and audit trail</Typography>
      </Box>

      {/* Activity table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>When</TableCell>
              <TableCell>Activity</TableCell>
              <TableCell sx={{ width: '12%' }}>Who</TableCell>
              <TableCell sx={{ width: '22%' }}>Date, Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activity.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
                  No activity found
                </TableCell>
              </TableRow>
            ) : (
              monthGroups.map(group => {
                const isExpanded = expandedMonths.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    {/* Month header row */}
                    <TableRow
                      onClick={() => toggleMonth(group.key)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#F8FAFC',
                        '&:hover': { backgroundColor: '#F1F5F9' },
                      }}
                    >
                      <TableCell colSpan={4} sx={{ py: 1.25, borderBottom: '1px solid #E2E8F0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isExpanded
                            ? <ExpandLessIcon sx={{ fontSize: 18, color: '#64748B' }} />
                            : <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748B' }} />
                          }
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                            {group.label}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                            · {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Entry rows (visible only when expanded) */}
                    {isExpanded && group.entries.map(entry => {
                      const info = buildActivity(entry);
                      const roleLabel = getRoleLabel(entry);
                      const roleColor = getRoleChipColor(roleLabel);
                      return (
                        <TableRow key={entry.id} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                          <TableCell>
                            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                              {formatTimeAgo(entry.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '6px',
                                backgroundColor: `${info.color}12`,
                                color: info.color,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                {info.icon}
                              </Box>
                              <Typography sx={{ fontSize: 13, color: '#1E293B', lineHeight: 1.5 }}>
                                {info.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={roleLabel}
                              size="small"
                              sx={{
                                backgroundColor: roleColor.bg,
                                color: roleColor.color,
                                fontWeight: 600,
                                fontSize: 11,
                                height: 24,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                              {formatDateTime(entry.created_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
          Showing {activity.length} of {total} entries
        </Typography>
        {activity.length < total && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            sx={{ fontSize: 12, borderRadius: '8px', borderColor: '#E2E8F0', color: '#475569' }}
          >
            {loadingMore ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
            Load More ({total - activity.length} remaining)
          </Button>
        )}
      </Box>
    </Box>
  );
}
